import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type AiConnection } from '../lib/api';
import { useSession } from '../context/SessionContext';
import { GuestGate } from '../components/GuestGate';

export function AiConnections() {
  const { authenticated } = useSession();
  const [connections, setConnections] = useState<AiConnection[]>([]);
  const [name, setName] = useState('OpenAI');
  const [credential, setCredential] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authenticated) return;
    void api
      .getAiConnections()
      .then((data) => setConnections(data.connections ?? data.items ?? []))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load connections'))
      .finally(() => setLoading(false));
  }, [authenticated]);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      const { connection } = await api.createAiConnection({
        name,
        credential,
        paysForUsage: true,
      });
      setConnections((prev) => [...prev, connection]);
      setCredential('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save connection');
    }
  }

  if (!authenticated) {
    return (
      <>
        <main className="page">
          <p className="muted">Sign in to manage GPT-6 Astra connections.</p>
        </main>
        <GuestGate open onDismiss={() => window.history.back()} />
      </>
    );
  }

  return (
    <main className="page">
      <h1 style={{ fontFamily: 'var(--font-display)' }}>AI connections</h1>
      <p className="muted">
        Model ID is fixed to <strong>gpt-6-astra</strong>. Disconnecting blocks new generation jobs.
      </p>

      {loading ? <p className="muted">Loading…</p> : null}
      {error ? <p className="err">{error}</p> : null}

      <section className="panel" style={{ marginTop: '1rem' }}>
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Connected</h2>
        {!connections.length && <p className="muted">No connections yet.</p>}
        <ul>
          {connections.map((connection) => (
            <li key={connection.id}>
              {connection.name} · {connection.modelId} · {connection.health}
            </li>
          ))}
        </ul>
      </section>

      <form className="panel" style={{ marginTop: '1rem', maxWidth: 520 }} onSubmit={onCreate}>
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Add connection</h2>
        <label className="field">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field" style={{ display: 'block', marginTop: '0.75rem' }}>
          Credential
          <input
            type="password"
            value={credential}
            onChange={(e) => setCredential(e.target.value)}
            placeholder="API key or credential ref"
          />
        </label>
        <button className="btn primary" type="submit" style={{ marginTop: '0.85rem' }}>
          Save connection
        </button>
      </form>

      <p style={{ marginTop: '1rem' }}>
        <Link className="btn" to="/create">
          Back to Create
        </Link>
      </p>
    </main>
  );
}
